import { Link } from "react-router-dom";
import InteractiveWorldMap from "../components/InteractiveWorldMap";
import IndexPageSplash from "../components/IndexPageSplash";
import { useSEO } from "../hooks/useSEO";

export default function Index() {
  useSEO({
    title: "Qicino Limited - Software Development & Cloud Services Nigeria",
    description: "Qicino Limited is Nigeria's leading software development company specializing in custom software, web apps, mobile development, cloud services (AWS, Azure, Google Cloud), and digital transformation solutions for businesses.",
    keywords: "Qicino Limited, software development Nigeria, custom software company Lagos, web development, mobile apps, cloud services, AWS Azure Google Cloud, digital transformation Nigeria",
    url: "https://qicino.com/",
  });

  return (
    <>
      <IndexPageSplash />
      <div className="min-h-screen bg-background">
      {/* Hero Section - Landwind Style */}
      <section className="bg-white dark:bg-gray-900 -mt-2">
        {/* Mobile World Map - First on Mobile */}
        <div className="lg:hidden w-full px-4 pt-2 pb-4">
          <div className="w-full h-[300px] sm:h-[350px]">
            <InteractiveWorldMap />
          </div>
        </div>

        <div className="container grid px-4 pt-4 pb-4 lg:gap-8 xl:gap-0 lg:pt-8 lg:pb-6 lg:grid-cols-12 h-full">
          {/* Left Column - Interactive World Map (Desktop Only) */}
          <div className="hidden lg:mt-0 lg:col-span-5 lg:flex lg:order-1 lg:items-center">
            <div className="w-full h-[400px] lg:h-[450px] xl:h-[500px]">
              <InteractiveWorldMap />
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="mr-auto place-self-center lg:col-span-7 lg:order-2 lg:pl-4 px-2 sm:px-0">
            <h1 className="max-w-2xl mb-4 text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-extrabold tracking-tight leading-none text-gray-900 dark:text-white">
              Professional software solutions for{" "}
              <span className="text-primary">growing businesses</span>
            </h1>
            <p className="max-w-2xl mb-6 font-light text-gray-500 lg:mb-8 text-base md:text-lg lg:text-xl dark:text-gray-400">
              Qicino Limited is a leading software development company
              that builds innovative digital solutions for small, medium, and
              large-scale businesses. We specialize in custom software
              development, cloud computing services, and comprehensive digital
              transformation.
            </p>

            <div className="flex flex-col mb-8 lg:mb-16 space-y-4 sm:flex-row sm:justify-start sm:space-y-0 sm:space-x-4">
              <Link
                to="/services"
                className="inline-flex justify-center items-center py-3 px-5 text-base font-medium text-center text-white rounded-lg bg-primary hover:bg-primary/90 focus:ring-4 focus:ring-primary/50 dark:focus:ring-primary/70"
              >
                Explore Services
                <svg
                  className="w-5 h-5 ml-2 -mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </Link>
              <Link
                to="/contact"
                className="inline-flex justify-center items-center py-3 px-5 text-base font-medium text-center text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 dark:text-white dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:ring-gray-800"
              >
                Get Started
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <div>
                <div className="flex items-center mb-1">
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                    50+
                  </h3>
                  <span className="text-primary ml-2">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Projects Delivered
                </p>
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                    24/7
                  </h3>
                  <span className="text-secondary ml-2">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Technical Support
                </p>
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                    5+
                  </h3>
                  <span className="text-primary ml-2">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                    </svg>
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Business Sectors
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by companies section */}
      <section className="bg-gray-50 dark:bg-gray-800">
        <div className="container py-8 lg:py-16 px-4">
          <h2 className="mb-6 lg:mb-8 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight text-center text-gray-900 dark:text-white">
            Trusted by leading companies worldwide
          </h2>
          {/* Sliding Logos Container */}
          <div className="relative overflow-hidden">
            <div className="flex animate-slide space-x-8 sm:space-x-12">
              {/* First set of logos */}
              <div className="flex items-center justify-center min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                <img
                  src="/images/nfonex.png"
                  alt="NFoneX Systems"
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-85 hover:opacity-100"
                />
              </div>

              <div className="flex items-center justify-center min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                <img
                  src="/images/Fits.png"
                  alt="Flash Telecoms"
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-85 hover:opacity-100"
                />
              </div>

              <div className="flex items-center justify-center min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                <img
                  src="/images/Antlers.svg"
                  alt="Antlers"
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-85 hover:opacity-100"
                />
              </div>

              <div className="flex items-center justify-center min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                <img
                  src="/images/Alkanes.png"
                  alt="Alkanese Engineering Services"
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-85 hover:opacity-100"
                />
              </div>

              <div className="flex items-center justify-center min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                <img
                  src="/images/sandcrete.png"
                  alt="Ssndcreet Engineering"
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-85 hover:opacity-100"
                />
              </div>

              <div className="flex items-center justify-center min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                <img
                  src="/images/shopify-logo.png"
                  alt="Shopify"
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-85 hover:opacity-100"
                />
              </div>

              {/* Duplicate set for seamless loop */}
              <div className="flex items-center justify-center min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                <img
                  src="/images/microsoft-logo.png"
                  alt="Microsoft"
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-85 hover:opacity-100"
                />
              </div>

              <div className="flex items-center justify-center min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                <img
                  src="/images/oracle-logo.png"
                  alt="Oracle"
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-85 hover:opacity-100"
                />
              </div>

              <div className="flex items-center justify-center min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                <img
                  src="/images/google-logo.png"
                  alt="Google"
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-85 hover:opacity-100"
                />
              </div>

              <div className="flex items-center justify-center min-w-[140px] sm:min-w-[160px] md:min-w-[200px]">
                <img
                  src="/images/amazon-logo.png"
                  alt="Amazon"
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-85 hover:opacity-100"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-white dark:bg-gray-900">
        <div className="container py-8 px-4 lg:py-16 lg:px-6">
          <div className="mx-auto max-w-screen-sm text-center lg:mb-16 mb-8">
            <h2 className="mb-4 text-3xl lg:text-4xl tracking-tight font-extrabold text-gray-900 dark:text-white">
              Our Expertise
            </h2>
            <p className="font-light text-gray-500 sm:text-xl dark:text-gray-400">
              Comprehensive solutions to accelerate your digital transformation
            </p>
          </div>

          <div className="grid gap-6 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <article className="p-4 sm:p-6 bg-green-50 rounded-lg border border-green-200 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-center mb-5 text-gray-500">
                <span className="bg-green-100 text-green-700 text-xs font-medium inline-flex items-center px-2.5 py-0.5 rounded">
                  <svg
                    className="mr-1 w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2 6a2 2 0 012-2h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"></path>
                  </svg>
                  Development
                </span>
              </div>
              <h2 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                <Link to="/services">Custom Software Development</Link>
              </h2>
              <p className="mb-5 font-light text-gray-500 dark:text-gray-400">
                Tailored web and mobile applications designed specifically for
                your business needs, built with modern technologies for optimal
                performance and scalability.
              </p>
              <div className="flex justify-between items-center">
                <Link
                  to="/services"
                  className="inline-flex items-center font-medium text-primary hover:underline"
                >
                  Learn more
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </Link>
              </div>
            </article>

            <article className="p-4 sm:p-6 bg-orange-50 rounded-lg border border-orange-200 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-center mb-5 text-gray-500">
                <span className="bg-orange-100 text-orange-700 text-xs font-medium inline-flex items-center px-2.5 py-0.5 rounded">
                  <svg
                    className="mr-1 w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"></path>
                  </svg>
                  Cloud
                </span>
              </div>
              <h2 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                <Link to="/services">Enterprise Cloud Solutions</Link>
              </h2>
              <p className="mb-5 font-light text-gray-500 dark:text-gray-400">
                Comprehensive cloud migration and infrastructure management
                services across Azure, AWS, Google Cloud, and Oracle Cloud
                platforms for businesses of all sizes.
              </p>
              <div className="flex justify-between items-center">
                <Link
                  to="/services"
                  className="inline-flex items-center font-medium text-primary hover:underline"
                >
                  Learn more
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </Link>
              </div>
            </article>

            <article className="p-4 sm:p-6 bg-blue-50 rounded-lg border border-blue-200 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-center mb-5 text-gray-500">
                <span className="bg-blue-100 text-blue-700 text-xs font-medium inline-flex items-center px-2.5 py-0.5 rounded">
                  <svg
                    className="mr-1 w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    ></path>
                    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"></path>
                  </svg>
                  Training
                </span>
              </div>
              <h2 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                <Link to="/training">Technical Training & Consulting</Link>
              </h2>
              <p className="mb-5 font-light text-gray-500 dark:text-gray-400">
                Professional development programs, cloud certifications, and
                technical consulting services to empower your team and drive
                digital transformation.
              </p>
              <div className="flex justify-between items-center">
                <Link
                  to="/training"
                  className="inline-flex items-center font-medium text-primary hover:underline"
                >
                  Learn more
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 dark:bg-gray-800">
        <div className="container py-8 px-4 sm:py-16 lg:px-6">
          <div className="mx-auto max-w-screen-sm text-center">
            <h2 className="mb-4 text-4xl tracking-tight font-extrabold leading-tight text-gray-900 dark:text-white">
              Transform Your Business Today
            </h2>
            <p className="mb-6 font-light text-gray-500 dark:text-gray-400 md:text-lg">
              Partner with Qicino Limited for professional software
              solutions that drive growth and innovation for your business.
            </p>
            <Link
              to="/contact"
              className="text-white bg-primary hover:bg-primary/90 focus:ring-4 focus:ring-primary/50 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:focus:ring-primary/70"
            >
              Get started today
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
